import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, Globe, Lightbulb, GraduationCap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import ArticleCard from "@/components/ArticleCard";
import { SEO, OrganizationSchema } from "@/components/SEO";
import api from "@/lib/api";

const THEME_CONFIG = {
  "bilinguisme-education-internationale": { icon: BookOpen, accent: "bg-ofa-blue", accentText: "text-ofa-blue", border: "border-ofa-blue/20" },
  "mobilite-internationale-transitions": { icon: Globe, accent: "bg-ofa-green", accentText: "text-ofa-green", border: "border-ofa-green/20" },
  "pedagogie-methodes": { icon: GraduationCap, accent: "bg-ofa-yellow", accentText: "text-ofa-yellow", border: "border-ofa-yellow/20" },
  "ia-innovation-education": { icon: Lightbulb, accent: "bg-ofa-purple", accentText: "text-ofa-purple", border: "border-ofa-purple/20" },
};

export default function HomePage() {
  const [featured, setFeatured] = useState([]);
  const [latest, setLatest] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [featRes, latestRes, catRes] = await Promise.all([
          api.get("/articles/featured?limit=1"),
          api.get("/articles?limit=6&page=1"),
          api.get("/categories"),
        ]);
        setFeatured(featRes.data);
        setLatest(latestRes.data.articles);
        setCategories(catRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, []);

  const heroArticle = featured[0];
  const restArticles = latest.filter((a) => a.slug !== heroArticle?.slug && !a.slug?.startsWith("ofa-live") && !a.slug?.startsWith("live-")).slice(0, 6);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
        <div className="animate-pulse space-y-8">
          <div className="h-80 bg-muted rounded-lg" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) => (<div key={i} className="h-40 bg-muted rounded-lg" />))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Le média de l'éducation bilingue et internationale"
        description="La plateforme éditoriale d'OFALycée : articles, analyses et ressources sur l'éducation bilingue, la mobilité internationale, la pédagogie innovante et l'IA dans l'éducation."
        url="/"
      />
      <OrganizationSchema />
      <div data-testid="homepage">

        {/* HERO SECTION */}
        <section className="text-white relative overflow-hidden min-h-[520px] flex items-center" data-testid="hero-banner">
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: "url('https://images.pexels.com/photos/9572637/pexels-photo-9572637.jpeg?auto=compress&cs=tinysrgb&w=1920&h=1080&fit=crop')" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a1628]/75 via-[#14274B]/50 to-[#14274B]/10" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(45,120,246,0.15),_transparent_60%)]" />
          <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative z-10">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <img src="/logo-white.png" alt="OFALycée" className="h-8" />
                <span className="text-sm font-mono tracking-widest uppercase text-white/50">Editorial</span>
              </div>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.1] mb-6">
                Le média de l'éducation<br className="hidden md:block" /> bilingue et internationale
              </h1>
              <p className="text-lg md:text-xl text-white/60 leading-relaxed mb-8 max-w-2xl">
                Analyses, témoignages et ressources pour accompagner les familles
                dans leurs choix éducatifs, par l'équipe d'OFALycée.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/blog">
                  <Button className="bg-ofa-blue hover:bg-ofa-blue/90 rounded-full px-6 py-3 text-white" data-testid="hero-explore-btn">
                    Explorer les articles <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/a-propos">
                  <Button variant="outline" className="rounded-full px-6 py-3 border-white/20 text-white hover:bg-white/10" data-testid="hero-about-btn">
                    En savoir plus
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* NOS THEMES SECTION */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-20" data-testid="themes-section">
          <div className="text-center mb-12">
            <span className="text-sm font-mono tracking-widest uppercase text-primary mb-3 block">Explorez nos thèmes</span>
            <h2 className="font-heading text-3xl md:text-4xl">Quatre axes éditoriaux</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat) => {
              const config = THEME_CONFIG[cat.slug] || { icon: BookOpen, accent: "bg-ofa-blue", accentText: "text-ofa-blue", border: "border-ofa-blue/20" };
              const Icon = config.icon;
              return (
                <Link
                  key={cat.id}
                  to={`/categorie/${cat.slug}`}
                  className={`group p-6 border ${config.border} hover:shadow-lg transition-all duration-300 bg-white`}
                  data-testid={`theme-card-${cat.slug}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${config.accent} flex items-center justify-center mb-4`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <h3 className="font-heading text-lg mb-2 group-hover:text-primary transition-colors">{cat.name}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3">{cat.description || ""}</p>
                  <span className={`text-xs font-medium ${config.accentText}`}>
                    {cat.article_count || 0} article{(cat.article_count || 0) > 1 ? "s" : ""} et lives
                  </span>
                </Link>
              );
            })}
          </div>
        </section>

        <Separator className="max-w-7xl mx-auto" />

        {/* ARTICLE A LA UNE */}
        {heroArticle && (
          <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="hero-article">
            <span className="text-sm font-mono tracking-widest uppercase text-primary mb-6 block">À la une</span>
            <ArticleCard article={heroArticle} variant="featured" />
          </section>
        )}

        <Separator className="max-w-7xl mx-auto" />

        {/* DERNIERS ARTICLES */}
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20" data-testid="latest-articles">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="text-sm font-mono tracking-widest uppercase text-primary mb-2 block">Dernières publications</span>
              <h2 className="font-heading text-3xl md:text-4xl">Nos articles récents</h2>
            </div>
            <Link to="/blog" className="hidden md:flex items-center gap-2 text-sm font-medium text-primary hover:underline underline-offset-4" data-testid="see-all-link">
              Voir tout <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-12 gap-y-16">
            {restArticles.map((article) => (<ArticleCard key={article.id} article={article} />))}
          </div>
          <div className="mt-12 md:hidden text-center">
            <Link to="/blog"><Button variant="outline" className="rounded-none" data-testid="see-all-mobile-btn">Voir tous les articles <ArrowRight className="h-4 w-4 ml-2" /></Button></Link>
          </div>
        </section>

        {/* QUI SOMMES-NOUS MINI */}
        <section className="bg-navy-50/50 py-16 md:py-20" data-testid="about-preview">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-sm font-mono tracking-widest uppercase text-primary mb-3 block">Qui sommes-nous</span>
                <h2 className="font-heading text-3xl md:text-4xl mb-6">
                  Éclairer les parcours éducatifs internationaux
                </h2>
                <p className="text-muted-foreground text-base md:text-lg leading-relaxed mb-6">
                  OFALycée Éditorial est la plateforme de contenus d'OFALycée,
                  le premier lycée franco-américain en ligne. Notre équipe éditoriale
                  produit des analyses, des témoignages et des ressources pour
                  accompagner les familles dans leurs choix éducatifs à l'international.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/a-propos" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline underline-offset-4" data-testid="about-link">
                    Découvrir notre mission <ArrowRight className="h-4 w-4" />
                  </Link>
                  <a href="https://www.youtube.com/@ofalycee" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-sm font-medium text-ofa-red hover:underline underline-offset-4" data-testid="youtube-link">
                    Notre chaîne YouTube <ArrowRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { value: "1\u00a0600+", label: "Élèves accompagnés" },
                  { value: "173", label: "Articles publiés" },
                  { value: "117", label: "Lives réalisés" },
                  { value: "15", label: "Nationalités" },
                ].map((stat, i) => (
                  <div key={i} className="bg-white p-6 text-center border border-border/50" data-testid={`stat-${i}`}>
                    <span className="block font-heading text-3xl text-navy-DEFAULT mb-1">{stat.value}</span>
                    <span className="text-sm text-muted-foreground">{stat.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* NEWSLETTER CTA */}
        <section className="bg-slate-50 border-y border-border py-16 md:py-20" data-testid="newsletter-cta-section">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <span className="text-sm font-mono tracking-widest uppercase text-ofa-blue mb-3 block">Newsletter</span>
            <h2 className="font-heading text-2xl md:text-3xl mb-4">Restez informés avec La Toile d'OFALycée</h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              Chaque mois, recevez nos articles de fond, interviews d'élèves et actualités de la communauté.
            </p>
            <a href="https://info.ofalycee.org/subscribe" target="_blank" rel="noopener noreferrer">
              <Button className="bg-ofa-blue hover:bg-ofa-blue/90 rounded-full px-8 py-3 text-white" data-testid="subscribe-home-btn">
                S'abonner gratuitement
              </Button>
            </a>
          </div>
        </section>

        {/* CTA OFALYCEE */}
        <section className="bg-[#14274B] text-white py-16 md:py-24" data-testid="cta-section">
          <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-6">Découvrez OFALycée</h2>
            <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto leading-relaxed">
              Une école en ligne premium, bilingue et internationale, pour les familles qui visent l'excellence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="https://ofalycee.org" target="_blank" rel="noopener noreferrer">
                <Button className="bg-ofa-blue hover:bg-ofa-blue/90 rounded-full px-8 py-3 text-white" data-testid="discover-cta-btn">Explorer les programmes</Button>
              </a>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full px-8 py-3 border-white/30 text-white hover:bg-white/10" data-testid="contact-cta-btn">Nous contacter</Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}
